// M2 economic & drilling rules as data + pure lookups (DESIGN §5.3, §5.5, §6.4, §12, §14).
// The sim agent implements the mechanics in docs/M2_SPEC.md and reads its numbers from here, so balance
// lives in one place. Nothing here touches state.
// Rates are approximate historical values (US corporate tax, Texas/Oklahoma production taxes, Texas
// Railroad Commission allowables), rounded for play.

// ------------------------------------------------------------------ generic step/linear lookups
// Step schedule [[year, value], ...] sorted: value of the last entry with year <= y (null before the first).
export function stepAt(schedule, y) {
  if (!schedule?.length || y < schedule[0][0]) return null;
  let v = schedule[0][1];
  for (const [yy, x] of schedule) if (y >= yy) v = x;
  return v;
}
// Linear keyframes [[year, value], ...], clamped at both ends.
export function lerpAt(keys, y) {
  if (!keys?.length) return 0;
  if (y <= keys[0][0]) return keys[0][1];
  if (y >= keys[keys.length - 1][0]) return keys[keys.length - 1][1];
  for (let i = 1; i < keys.length; i++) {
    if (y <= keys[i][0]) {
      const [y0, v0] = keys[i - 1], [y1, v1] = keys[i];
      return v0 + (v1 - v0) * (y - y0) / (y1 - y0);
    }
  }
  return keys[keys.length - 1][1];
}

// ------------------------------------------------------------------ federal income tax (DESIGN §6.4)
// Corporate normal tax on net income (step schedule). DESIGN's 1%→12%→38% keyframes, filled in.
export const CORPORATE_TAX = [
  [1909, 0.01], // corporate excise tax
  [1913.75, 0.01], [1916, 0.02], [1917, 0.06], [1918, 0.12], [1919, 0.10], [1922, 0.125], [1925, 0.13],
  [1926, 0.135], [1928, 0.12], [1929, 0.11], [1930, 0.12], [1932, 0.1375], [1936, 0.15], [1938, 0.19],
  [1940, 0.24], [1941, 0.31], [1942, 0.40], [1946, 0.38],
];
// War excess-profits tax: extra rate on profit above `normalReturn` x net assets (annualized).
export const EXCESS_PROFITS = [
  { from: 1917.25, until: 1922.0, rate: 0.3, normalReturn: 0.08 },
  { from: 1940.75, until: 1946.0, rate: 0.5, normalReturn: 0.08 },
];
export const corporateTaxRate = (y) => stepAt(CORPORATE_TAX, y) ?? 0;
export function excessProfitsTax(y) { return EXCESS_PROFITS.find((e) => y >= e.from && y < e.until) ?? null; }

// Percentage depletion (Revenue Act of 1926): deduct rate x gross oil revenue, capped at netCap x net income.
// Before 1926 a small cost-depletion stand-in. Ends 1975 (M3: small producers only after that).
export const DEPLETION = [[1913.75, { rate: 0.05, netCap: 0.5 }], [1926.15, { rate: 0.275, netCap: 0.5 }], [1975, { rate: 0, netCap: 0 }]];
export const depletionAllowance = (y) => stepAt(DEPLETION, y) ?? { rate: 0, netCap: 0 };

// Intangible drilling costs (wages, mud, fuel, rig day-rate) are expensed in the year incurred (Treasury, 1917).
export const IDC_EXPENSED_FROM = 1917;
// Capex (rigs, engines, tanks, pipe) depreciates straight-line over this many years for tax purposes.
export const DEPRECIATION_YEARS = 5;

// ------------------------------------------------------------------ state severance / production taxes
// Fraction of gross value at the wellhead, by region (step schedules).
export const SEVERANCE = {
  texas:    [[1905.3, 0.01], [1907, 0.015], [1919, 0.02], [1933, 0.0275], [1951, 0.046]],
  oklahoma: [[1910, 0.03], [1935, 0.05]],
  mexico:   [[1912, 0.03], [1917, 0.10], [1921, 0.15]],
};
export const severanceRate = (regionId, y) => stepAt(SEVERANCE[regionId], y) ?? 0;

// ------------------------------------------------------------------ prorationing (DESIGN §5.5)
// Each group: `from` (fractional year), and either perWell (bbl/d per well, keyframes, linear) or
// fractionOfPotential (keyframes, linear), plus an optional flag that must be set for it to apply.
export const PRORATION = {
  east_texas: { from: 1931.67, flag: null, perWell: [[1931.67, 225], [1932, 150], [1932.5, 75], [1933.5, 40], [1935, 22], [1940, 20], [1942, 28], [1945, 30]] },
  texas:      { from: 1931.67, flag: 'prorationing', fractionOfPotential: [[1931.67, 0.4], [1935, 0.3], [1941.9, 0.45], [1943, 0.65], [1945.6, 0.5]] },
  yates:      { from: 1927.67, flag: null, fractionOfPotential: [[1927.67, 0.08], [1931, 0.05], [1935, 0.06], [1943, 0.12]] },
  oklahoma:   { from: 1928.67, flag: 'prorationingOK', fractionOfPotential: [[1928.67, 0.6], [1931.6, 0.35], [1933, 0.4], [1941.9, 0.6], [1943, 0.75]] },
  california: { from: 1929.5, flag: null, voluntary: true, fractionOfPotential: [[1929.5, 0.7], [1935, 0.65], [1942, 0.85]] },
};
// Minimum daily allowable for small wells (stripper exemption): never prorated below this.
export const STRIPPER_BBLD = 10;

// Allowable bbl/d for one well with natural `potential` bbl/d. flags: state.flags (or {}).
// Returns potential when no proration applies.
export function allowable(groupId, y, potential, flags = {}) {
  const g = PRORATION[groupId];
  if (!g || y < g.from || (g.flag && !flags[g.flag])) return potential;
  if (potential <= STRIPPER_BBLD) return potential;
  const cap = g.perWell ? lerpAt(g.perWell, y) : potential * lerpAt(g.fractionOfPotential, y);
  return Math.max(Math.min(STRIPPER_BBLD, potential), Math.min(potential, cap));
}

// ------------------------------------------------------------------ hot oil (DESIGN §5.5, see M2_SPEC §4)
// Oil produced above the allowable is "hot". It sells only to hot-oil refiners at a discount, and every
// month a field runs hot there is a chance the Commission's (later federal) inspectors catch it.
export const HOT_OIL = {
  priceMult: 0.75,              // bootleg buyers pay 75% of the local price
  bustPerMonth: 0.05,           // chance of being caught each month a field produced hot oil
  bustPerMonthFederal: 0.10,    // while flag hotOilFederal (1933-35) or connallyAct (1935+) is set
  finePerBbl: 0.5,              // base-year (1931) $ per hot barrel produced in the last 90 days
  fineMultFederal: 3,
  reputation: -5,
  shutInDays: 30,               // every well on the field shut in
  baseYear: 1931,
};

// ------------------------------------------------------------------ contract drilling (M2_SPEC §1.7)
// Drill-by-the-foot contractors (like the Hamill brothers at Spindletop, $2/ft): the poor wildcatter's entry
// to the rotary era. Base-year $ per ft by year (CPI-scale with inflate()).
export const CONTRACT_DRILLING = { perFt: [[1901, 2.0], [1910, 2.5], [1920, 4.0], [1930, 3.0], [1940, 3.5], [1945, 4.5]], baseYear: 1901, maxDepthFt: [[1901, 1500], [1915, 4000], [1928, 8000], [1940, 10000]], mobilizationDays: 10 };
export const contractPerFt = (y) => lerpAt(CONTRACT_DRILLING.perFt, y);
export const contractMaxDepth = (y) => stepAt(CONTRACT_DRILLING.maxDepthFt, y) ?? 0;

// ------------------------------------------------------------------ rotary drilling constants (M2_SPEC §1-3)
export const ROTARY = {
  wobRange: [0.5, 1.6], rpmRange: [0.5, 1.6],
  ropRpmExpSoft: 0.6, ropRpmExpHard: 0.3, hardThreshold: 6,
  wearRpmExp: 1.3, bearingRpmExp: 1.5,
  rpmRiskSlope: 1.5, rpmRiskKnee: 1.2,
  ballingWobKnee: 1.1, ballingPenalty: 0.5,       // ROP x (1 - penalty x (wob-knee) x balling x (1-cake))
  deviationRpmExp: 0.5,                            // deviation growth / rpm^0.5 (fast & light = straight)
  noPumpRopMult: 0.25,                             // rotary with no mud pump: cuttings regrind
  hydraulicsExp: 0.3,                              // hydF = min(1, pumpRatedDepth/depth)^exp x pump.hydraulicsMult
  heavingStableDaysMud: 30,                        // a heaving stratum with mud >= 8.8 ppg behaves as stableDays 30 x cake
};
export const MUD = {
  swabPpg: 0.3,               // effective MW reduction while tripping out (swab kicks)
  ecdPerKft: 0.08,            // equivalent circulating density above static MW per 1,000 ft while drilling
  kickPerPpg: 0.25,           // kick chance/day = min(0.9, kickPerPpg x (pore - MWeff)) per permeable zone
  gasKickMult: 1.5,
  killMarginPpg: 0.3,         // kill weight = pore + margin
  bariteCostPerTenthPpgFt: 0.05, baritePriceYear: 1930, bariteAboveppg: 9.5,
  lostCircMudCostMult: 10, lostCircStuckPerDay: 0.05,
  diffStickPerPpgDay: 0.004,  // per ppg of overbalance above diffStickFreePpg across permeable sands, x (1-cake)
  diffStickFreePpg: 1.5,
  overbalanceShowPenalty: 0.4, // chance to MISS an oil show per ppg overbalance (rotary only; e-logs/cores fix it)
};
export const PIPE = {
  jointFt: 30,
  twistOffThreshold: 70,      // fatigue % above which twist-offs start (DESIGN §3.4)
  twistOffPerPoint: 0.002,    // chance/day per fatigue point over threshold
  inspectCostPerJoint: 1.0, inspectBaseYear: 1925, inspectCullFrac: 0.1, inspectResetTo: 30,
  deviationFatigueMult: 0.2,  // fatigue x (1 + deviationDeg x 0.2)
};

// ------------------------------------------------------------------ expropriation / political risk (M2_SPEC §10)
// Region risk `expropriation` is an ANNUAL probability; convert to a daily hazard.
export const dailyFromAnnual = (p) => 1 - Math.pow(1 - Math.min(0.999999, Math.max(0, p)), 1 / 365);
export const POLITICAL_RISK_INSURANCE = { fromYear: 1919, premiumMult: 1.3, payout: 0.75, maxCoverYears: 5 };

// ------------------------------------------------------------------ rival drainage (rule of capture)
// Fraction of a reservoir's REMAINING recoverable oil drained per year by other operators (reservoir.rivalDrain).
export const rivalDrainRate = (res, y) => (res?.rivalDrain ? Math.max(0, lerpAt(res.rivalDrain, y)) : 0);
// Daily multiplier applied to reservoir.remaining.
export const rivalDrainDailyMult = (res, y) => Math.exp(-rivalDrainRate(res, y) / 365);

// ------------------------------------------------------------------ conservation (waste) fines from 1930
// Gushing to pits / flaring after the conservation laws: fine per bbl wasted (base 1931 $).
export const WASTE = { from: 1930.0, finePerBbl: 0.05, baseYear: 1931, flareFinePerDay: 25, flareFlag: 'flaringLimits' };
